import React, { ComponentProps, ReactNode, memo } from 'react'

import styled from '@emotion/styled'

import { isString } from '@surglogs/util'

import { Button } from './Button'
import { FlexBox } from './FlexBox'
import { Icon } from './Icon'
import { Text, TextBold } from './Text'

type BoxButtonVariant = 'horizontal' | 'vertical'

const StyledButton = styled(Button)`
  white-space: normal;

  &:focus {
    box-shadow: none;
  }

  // override button styles
  & svg {
    height: auto;
    width: auto;
  }
`

interface BoxButtonProps
  extends Omit<ComponentProps<typeof StyledButton>, 'variant' | 'children'> {
  title: string
  description: ReactNode
  iconSize?: ComponentProps<typeof Icon>['size']
  variant?: BoxButtonVariant
  customIcon?: ReactNode
  pill?: ReactNode
}

export const BoxButton = memo(
  ({
    title,
    description,
    onClick,
    icon,
    iconSize,
    variant = 'horizontal',
    customIcon,
    pill,
    ...rest
  }: BoxButtonProps) => {
    const isColumn = variant === 'vertical'

    return (
      <StyledButton
        {...{
          'aria-label': title,
          onClick,
          variant: 'secondary',
          h: 'unset',
          padding: 'xl',
          w: '100%',
          gap: isColumn ? 'sm' : 'md',
          fd: isColumn ? 'column' : 'row',
          flex: 1,
          ...rest,
        }}>
        {customIcon ? customIcon : null}
        {icon ? <Icon {...{ icon, color: 'inherit', size: iconSize }} /> : null}
        <FlexBox
          {...{
            fd: 'column',
            flex: 1,
            ta: isColumn ? 'center' : 'left',
            ai: isColumn ? 'center' : 'flex-start',
          }}>
          <FlexBox
            {...{
              ai: 'center',
              gap: 'sm',
              mb: 'xs',
            }}>
            <TextBold {...{ color: 'inherit' }}>{title}</TextBold>
            {pill ?? null}
          </FlexBox>
          {isString(description) ? (
            <Text {...{ color: 'inherit', size: 'xxs' }}>{description}</Text>
          ) : (
            description
          )}
        </FlexBox>
      </StyledButton>
    )
  },
)
